import { FC } from 'react';
import classNames from 'classnames';

import { NumberSignEnum } from '../../../../../lib/enums';
import { CharacterEnum } from '../../../../../lib/enums/character.enum';
import lang from '../../../../../lib/language';
import TradePriceCache from '../../../../../lib/store-util/TradePriceCache';
import {
  formatCompactMetricValue,
  formatCurrencyBySymbol,
  formatNumberWithSuffix,
  getLimitedText,
} from '../../../../../lib/util/DataHelpers';

import CompanyLogo from '../../../../../components/CompanyLogo/CompanyLogo';
import { ChartLine } from '../../../../ChartLine';
import ClickableDiv from '../../../../ClickableDiv/ClickableDiv';
import useIsMobile from '../../../../hooks/useIsMobile';
import { MARKET_CAP_RADAR_INTERNAL_NAME_LIMIT } from '../../../MarketCapRadar.constants';
import { MarketCapRadarInternalRowProps } from '../../MarketCapRadarInternal.types';

import styles from '../../MarketCapRadarInternal.module.scss';

const MarketCapRadarInternalRow: FC<MarketCapRadarInternalRowProps> = ({ row, onSymbolClick }) => {
  const { symbol, lookupSymbol, currencySymbol, logo, logoUrl, displayName } = row;
  const { isMobile } = useIsMobile();

  const { currentPrice, changePercent, direction } = TradePriceCache.use(lookupSymbol, 'MarketCapRadarInternal');

  // Without a live price the row falls back to the scanner response, so its colour has to
  // come from that same value rather than from the (still empty) live direction.
  const isNegativeChange = currentPrice == null
    ? (row.changesPercentage ?? 0) < 0
    : direction === NumberSignEnum.Negative;
  const changePercentSign = isNegativeChange ? CharacterEnum.Empty : NumberSignEnum.Positive;
  const resolvedChangePercent = currentPrice == null
    ? `(${changePercentSign}${formatNumberWithSuffix(row.changesPercentage, CharacterEnum.Percent)})`
    : `(${direction}${changePercent})`;
  const lastPrice = formatCurrencyBySymbol(currentPrice ?? row.price, currencySymbol);
  const marketCap = formatCompactMetricValue(row.marketCap, { withCurrency: true });
  const changePercentClassName = classNames(styles.cellValue, {
    [styles.negative]: isNegativeChange,
    [styles.positive]: !isNegativeChange,
  });

  const companyLogo = <CompanyLogo url={logoUrl} logo={logo} symbol={symbol} />;

  if (isMobile) {
    return (
      <ClickableDiv className={styles.mobileRow} onClick={() => onSymbolClick(symbol)}>
        <div className={styles.mobileIdentity}>
          {companyLogo}
          <div className={styles.mobileIdentityText}>
            <span className={styles.mobileSymbol}>{symbol}</span>
            <span className={styles.mobileMarketCap}>
              {lang.marketCapRadarMobileMarketCap(marketCap)}
            </span>
          </div>
        </div>
        <div className={styles.mobileChart}>
          <ChartLine symbol={lookupSymbol} />
        </div>
        <div className={styles.mobilePrices}>
          <span className={styles.cellValue}>{lastPrice}</span>
          <span className={changePercentClassName}>{resolvedChangePercent}</span>
        </div>
      </ClickableDiv>
    );
  }

  return (
    <ClickableDiv className={styles.tableRow} onClick={() => onSymbolClick(symbol)}>
      <div className={styles.companyCell}>
        {companyLogo}
        <span className={styles.companySymbol}>{symbol}</span>
        <span className={styles.companyName}>
          {getLimitedText(displayName, MARKET_CAP_RADAR_INTERNAL_NAME_LIMIT)}
        </span>
      </div>
      <div className={styles.chartCell}>
        <ChartLine symbol={lookupSymbol} />
      </div>
      <div className={styles.cellValue}>{marketCap}</div>
      <div className={styles.cellValue}>{formatCompactMetricValue(row.volume)}</div>
      <div className={styles.cellValue}>{formatNumberWithSuffix(row.volumePercentage, CharacterEnum.Percent)}</div>
      <div className={styles.cellValue}>{lastPrice}</div>
      <div className={changePercentClassName}>{resolvedChangePercent}</div>
    </ClickableDiv>
  );
};

export default MarketCapRadarInternalRow;
